<template>
  <div
    class="tcs"
    :class="stateClass"
    :role="isClaimable ? 'button' : undefined"
    :tabindex="isClaimable ? 0 : undefined"
    @click="onClick"
    @keydown.enter.prevent="onClick"
  >
    <div class="tcl">{{ level.label }}</div>
    <div class="tcb">{{ requirementLabel }} {{ fmtRequirement(level.requirement) }}</div>
    <div class="tcr">{{ fmtReward(level.reward) }}</div>
    <div class="tcstat">{{ statusText }}</div>
  </div>
</template>

<script setup lang="ts">
import { formatBaseAmt } from '~/utils/transfer/money'
import type { DailyTask, DailyTaskLevel, DailyTaskMetric } from '../types'
import { TASK_TYPE_META } from '../taskTypeMeta'

const props = defineProps<{
  level: DailyTaskLevel
  metric?: DailyTaskMetric
  taskType?: DailyTask['type']
}>()
const emit = defineEmits<{ action: [] }>()

const { t } = useI18n()
const userStore = useUserStore()
const fmtMoney = (n: number) => formatBaseAmt(n, userStore.baseCurrency)
const unit = computed(() => {
  const key = props.taskType && TASK_TYPE_META[props.taskType].unitKey
  return key ? t(key) : ''
})
const fmtRequirement = (n: number) =>
  props.metric === 'count'
    ? `${n.toLocaleString()}${unit.value ? ` ${unit.value}` : ''}`
    : fmtMoney(n)
const fmtReward = (n: number) => fmtMoney(n)

const requirementLabel = computed(() => {
  if (props.taskType) return t(TASK_TYPE_META[props.taskType].labelKey)
  if (props.metric === 'deposit') return t('promo.daily.requirementDeposit')
  if (props.metric === 'count') return t('promo.daily.requirementCount')
  return t('promo.daily.requirementBet')
})

const stateClassMap: Record<DailyTaskLevel['state'], string> = {
  claimed: 'done',
  claimable: 'claim',
  progress: 'cur',
  locked: 'lock',
}

const hasClaimId = computed(() => !!props.level.claimId)

const stateKey = computed(() =>
  props.level.state === 'claimable' && hasClaimId.value ? 'claimed' : props.level.state,
)

const stateClass = computed(() => stateClassMap[stateKey.value])
const statusText = computed(() =>
  stateKey.value === 'claimed'
    ? t('promo.daily.statusClaimed')
    : stateKey.value === 'claimable'
      ? t('promo.daily.statusClaimable')
      : stateKey.value === 'progress'
        ? t('promo.daily.statusProgress')
        : t('promo.daily.statusLocked'),
)

const isClaimable = computed(() => props.level.action === 'CLAIM' && !props.level.claimId)

/** 结果提示由页面按领取结果统一弹（已领取 / 处理中 / 风控），这里不再提前弹「成功」 */
const onClick = () => {
  if (!isClaimable.value) return
  emit('action')
}
</script>

<style scoped lang="scss">
.tcs {
  flex: 0 0 auto;
  min-width: 88px;
  padding: 9px 10px;
  border-radius: var(--r-md);
  background: var(--surface2);
  border: 1px solid var(--hairline);
  text-align: center;
  position: relative;
  min-width: 92px;
  opacity: 0.6;
}

.tcl {
  font-size: 10px;
  color: var(--muted);
}

.tcb {
  font-size: 11px;
  font-weight: 600;
  margin: 3px 0;
  white-space: nowrap;
}

.tcr {
  font-size: 15px;
  font-weight: 900;
  color: var(--muted);
}

.tcs.done .tcr {
  color: var(--success);
}

.tcstat {
  font-size: 9px;
  font-weight: 800;
  margin-top: 5px;
  padding: 2px 0;
  border-radius: var(--r-xs);
}

.tcs.done .tcstat {
  color: var(--success);
}

.tcs.done {
  opacity: 0.6;
}

.tcs.done .tcstat {
  color: var(--success);
}

.tcs.claim {
  opacity: 1;
  cursor: pointer;
  transition: transform 0.15s ease-out;
}

.tcs.claim:active {
  transform: scale(0.97);
  transition-duration: 0.06s;
}

.tcs.claim .tcr {
  color: var(--gold-mid);
}

.tcs.claim .tcstat {
  color: var(--gold-ink);
  background: linear-gradient(135deg, var(--gold-hi), var(--gold-amber));
}

.tcs.cur {
  opacity: 1;
}

.tcs.lock {
  opacity: 0.4;
}

.tcs.cur .tcstat {
  color: var(--primary);
}

.tcs.cur .tcr {
  color: var(--primary);
}

.tcs.cur .tcstat {
  color: var(--primary);
}

.tcs.cur {
  border-color: var(--primary);
  background: rgba(var(--primary-rgb), 0.14);
}

.tcs.lock {
  opacity: 0.4;
}

.tcs.lock .tcstat {
  color: var(--muted);
}
</style>
